const textEncoder = new TextEncoder();
const crc32Table = new Uint32Array(256);

for (let index = 0; index < crc32Table.length; index += 1) {
  let value = index;
  for (let bit = 0; bit < 8; bit += 1) {
    value = (value >>> 1) ^ (value & 1 ? 0xedb88320 : 0);
  }
  crc32Table[index] = value >>> 0;
}

function writeHeader(byteLength, writeValues) {
  const bytes = new Uint8Array(byteLength);
  const view = new DataView(bytes.buffer);
  writeValues(view);
  return bytes;
}

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = (crc >>> 8) ^ crc32Table[(crc ^ bytes[index]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

async function decompressZipEntry(compressedBytes, method) {
  if (method === 0) return compressedBytes;
  if (method !== 8) {
    throw new Error(`ZIP compression method ${method} is not supported.`);
  }
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser cannot decompress compressed ZIP files.");
  }
  try {
    const stream = new Blob([compressedBytes])
      .stream()
      .pipeThrough(new DecompressionStream("deflate-raw"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch (error) {
    throw new Error("Could not decompress an audio file from the ZIP.");
  }
}

export async function parseBatchAudioZip(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const minimumEndRecord = Math.max(0, bytes.length - 22 - 0xffff);
  let endRecordOffset = -1;

  for (let offset = bytes.length - 22; offset >= minimumEndRecord; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      endRecordOffset = offset;
      break;
    }
  }
  if (endRecordOffset < 0) throw new Error("The selected file is not a valid ZIP.");

  const entryCount = view.getUint16(endRecordOffset + 10, true);
  let cursor = view.getUint32(endRecordOffset + 16, true);
  const decoder = new TextDecoder();
  const entries = [];

  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(cursor, true) !== 0x02014b50) {
      throw new Error("The ZIP directory is invalid or incomplete.");
    }
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const expectedCrc = view.getUint32(cursor + 16, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const filenameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localHeaderOffset = view.getUint32(cursor + 42, true);
    const filename = decoder.decode(
      bytes.subarray(cursor + 46, cursor + 46 + filenameLength),
    );
    cursor += 46 + filenameLength + extraLength + commentLength;

    if (filename.endsWith("/")) continue;
    if (flags & 0x0001) {
      throw new Error("Password-protected ZIP files are not supported.");
    }
    if (view.getUint32(localHeaderOffset, true) !== 0x04034b50) {
      throw new Error(`The ZIP entry ${filename} is invalid.`);
    }
    const localFilenameLength = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
    const dataOffset = localHeaderOffset + 30 + localFilenameLength + localExtraLength;
    const compressedBytes = bytes.subarray(dataOffset, dataOffset + compressedSize);
    const content = await decompressZipEntry(compressedBytes, method);
    if (crc32(content) !== expectedCrc) {
      throw new Error(`The ZIP entry ${filename} is corrupted.`);
    }
    entries.push({ name: filename, blob: new Blob([content]) });
  }

  return entries;
}

export function sanitizeBatchAudioFilenamePart(value = "") {
  return String(value || "")
    .normalize("NFKC")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/[. ]+$/g, "")
    .trim()
    .slice(0, 120) || "untitled";
}

export async function createBatchAudioZip(entries = []) {
  const localParts = [];
  const centralParts = [];
  let localOffset = 0;

  for (const entry of entries) {
    const filename = textEncoder.encode(String(entry?.name || "audio.mp3"));
    const fileBytes = new Uint8Array(await entry.blob.arrayBuffer());
    const checksum = crc32(fileBytes);
    const localHeader = writeHeader(30, (view) => {
      view.setUint32(0, 0x04034b50, true);
      view.setUint16(4, 20, true);
      view.setUint16(6, 0x0800, true);
      view.setUint16(8, 0, true);
      view.setUint16(10, 0, true);
      view.setUint16(12, 0x0021, true);
      view.setUint32(14, checksum, true);
      view.setUint32(18, fileBytes.length, true);
      view.setUint32(22, fileBytes.length, true);
      view.setUint16(26, filename.length, true);
      view.setUint16(28, 0, true);
    });
    localParts.push(localHeader, filename, fileBytes);

    const centralHeader = writeHeader(46, (view) => {
      view.setUint32(0, 0x02014b50, true);
      view.setUint16(4, 20, true);
      view.setUint16(6, 20, true);
      view.setUint16(8, 0x0800, true);
      view.setUint16(10, 0, true);
      view.setUint16(12, 0, true);
      view.setUint16(14, 0x0021, true);
      view.setUint32(16, checksum, true);
      view.setUint32(20, fileBytes.length, true);
      view.setUint32(24, fileBytes.length, true);
      view.setUint16(28, filename.length, true);
      view.setUint16(30, 0, true);
      view.setUint16(32, 0, true);
      view.setUint16(34, 0, true);
      view.setUint16(36, 0, true);
      view.setUint32(38, 0, true);
      view.setUint32(42, localOffset, true);
    });
    centralParts.push(centralHeader, filename);
    localOffset += localHeader.length + filename.length + fileBytes.length;
  }

  const centralDirectorySize = centralParts.reduce(
    (size, part) => size + part.length,
    0,
  );
  const endOfCentralDirectory = writeHeader(22, (view) => {
    view.setUint32(0, 0x06054b50, true);
    view.setUint16(4, 0, true);
    view.setUint16(6, 0, true);
    view.setUint16(8, entries.length, true);
    view.setUint16(10, entries.length, true);
    view.setUint32(12, centralDirectorySize, true);
    view.setUint32(16, localOffset, true);
    view.setUint16(20, 0, true);
  });

  return new Blob([...localParts, ...centralParts, endOfCentralDirectory], {
    type: "application/zip",
  });
}
